import { createQueryKeyStore } from '@lukemorales/query-key-factory';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { BrandguideResults } from '~components/search/BrandguideResults';
import { fetchSearchResult } from '~services/api/calls/search';
import { addPrefixToRelativeLink } from '~services/location-helpers';
import stripHtml from '~utils/stripHtml';

export const queries = createQueryKeyStore({
  search: {
    search: (query: string) => ({
      queryKey: [{ query }],
      queryFn: async (pageParam: number) => makeSearchResult(await fetchSearchResult(query, pageParam)),
    }),
  },
});

export function BrandguideResultsController() {
  const { term } = useParams();

  const { data, isLoading, isError, fetchNextPage } = useInfiniteQuery<any, Error>({
    ...queries.search.search(term),
    refetchOnWindowFocus: false,
    initialPageParam: 1,
    getNextPageParam(lastPage) {
      const next = lastPage.next || -1;
      if (next < 0) return;
      return next;
    },
  });

  // derived data
  const resultList = (data?.pages || []).reduce<SearchResultsItem[]>((prev, curr) => {
    return prev.concat(curr.result.results);
  }, []);
  const numFound = data?.pages.at(0).result.numFound ?? 0;

  return (
    <BrandguideResults
      isLoading={isLoading}
      isError={isError}
      resultList={resultList}
      numFound={numFound}
      onLoadMore={fetchNextPage}
    />
  );
}

function sanitizeBreadcrumbItem(item: SearchResultsItemBreadcrumbItem): SearchResultsItemBreadcrumbItem {
  return {
    title: stripHtml(item.title),
    url: addPrefixToRelativeLink(item.url),
  };
}

function sanitizeResultsItem(item: SearchResultsItem): SearchResultsItem {
  return {
    ...item,
    title: stripHtml(item.title),
    url: addPrefixToRelativeLink(item.url),
    pathPages: item.pathPages.map(sanitizeBreadcrumbItem),
  };
}

function makeSearchResult(dto: SearchDto): SearchDto {
  return {
    ...dto,
    result: {
      ...dto.result,
      results: dto.result.results ? dto.result.results.map(sanitizeResultsItem) : [],
    },
  };
}
